// pages/monitor/panels/peak/PeakControl.js —— 削峰填谷 · 控制中心（依据《云平台单站控制（削峰填谷）需求文档》p25-40）
// 覆盖：策略管理（策略启停与切换 / 手动策略 / 预置策略 / 日前策略[独有]）、设备控制（差异 #8）、
//       电池保护、需量保护、逆功率保护、高级功能。
// 差异 #10：本类型独有「日前策略」；差异 #3：预置策略模板支持按月配置（策略功率覆盖时间范围不跨类型借用）。
import React, { useState } from 'react';
import ScrollTabs from '../../../../components/ScrollTabs';
import { Segmented, Switch, Radio, Checkbox, InputNumber, Input, Table, Select, Button, Space, Tag, message,
} from 'antd';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import {
  getPresetStrategy, getStrategyCurve, getDayAhead, getProtection, getControlActions,
  getAdvancedFeatures, getDevices, TARIFF,
} from '../../../../config/stationData';
import { PRESET_MONTHS } from './data';
import './peak.css';

const TICK = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const TT = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const LEGEND = { wrapperStyle: { fontSize: 11, color: 'rgba(255,255,255,0.6)' }, iconSize: 8 };
const GRID = 'rgba(255,255,255,0.08)';

const TariffTags = () => (
  <Space size={8} wrap>
    <span className="tariff-tag valley">谷 {TARIFF.valley.price}</span>
    <span className="tariff-tag flat">平 {TARIFF.flat.price}</span>
    <span className="tariff-tag peak">峰 {TARIFF.peak.price}</span>
    <span className="tariff-tag sharp">尖 {TARIFF.sharp.price}</span>
  </Space>
);

// ---------------- 策略管理 ----------------
const StrategyView = ({ station }) => {
  const [mode, setMode] = useState('preset');
  const [on, setOn] = useState(station.strategyOn);
  const [month, setMonth] = useState('10 月');
  const presets = getPresetStrategy();
  const curve = getStrategyCurve();
  const dayAhead = getDayAhead();
  const [manual, setManual] = useState([
    { key: 1, start: '00:00', end: '06:00', power: 300, state: '充电' },
    { key: 2, start: '08:00', end: '11:00', power: -250, state: '放电' },
    { key: 3, start: '19:00', end: '22:00', power: -280, state: '放电' },
  ]);
  const [daMode, setDaMode] = useState('fixed');
  const [da, setDa] = useState({ chargePrice: dayAhead.chargePrice, dischargePrice: dayAhead.dischargePrice, idleAction: dayAhead.idleAction });

  return (
    <div>
      <div className="peak-toolbar">
        <span className="peak-toolbar-label">策略启停</span>
        <Switch checked={on} onChange={(v) => { setOn(v); message.success(v ? '策略已启用' : '策略已停止'); }} />
        <Segmented value={mode} onChange={setMode}
          options={[{ label: '手动策略', value: 'manual' }, { label: '预置策略', value: 'preset' }, { label: '日前策略', value: 'dayahead' }]} />
        <Tag color={on ? 'cyan' : 'default'}>{on ? '运行中' : '已停止'}</Tag>
      </div>

      {mode === 'preset' ? (
        <div>
          <div className="peak-toolbar">
            <span className="peak-toolbar-label">按月配置模板</span>
            <Select size="small" value={month} onChange={setMonth} style={{ width: 120 }} options={PRESET_MONTHS.map((m) => ({ value: m, label: m }))} />
            <span className="peak-toolbar-label">依据分时电价设定各时段 PCS 充放功率；不同月份分时电价不同，可分别配置。</span>
          </div>
          <Table size="small" rowKey="key" pagination={false} dataSource={presets}
            columns={[
              { title: '时段', render: (_, r) => `${r.start} ~ ${r.end}` },
              { title: '功率 (kW)', dataIndex: 'power' },
              { title: '状态', dataIndex: 'state', render: (v) => <Tag color={v === '充电' ? 'cyan' : v === '放电' ? 'green' : 'default'}>{v}</Tag> },
              { title: '功率分配规则', dataIndex: 'rule' },
              { title: 'PCS', dataIndex: 'pcs' },
            ]}
          />
          <div className="panel-title" style={{ marginTop: 16 }}>预置策略曲线<small>时段划分 · 阶梯</small></div>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={curve} margin={{ top: 10, right: 16, bottom: 0, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="clock" interval={5} tick={TICK} axisLine={false} tickLine={false} />
              <YAxis tick={TICK} axisLine={false} tickLine={false} width={50} />
              <Tooltip contentStyle={TT} />
              <Legend {...LEGEND} />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" />
              <Line type="stepAfter" dataKey="power" name="计划功率 kW" stroke="#52e8b0" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      {mode === 'manual' ? (
        <div>
          <div className="peak-toolbar">
            <span className="peak-toolbar-label">手动设定各时段 PCS 充放功率</span>
            <Button size="small" icon={<span>+</span>} onClick={() => setManual([...manual, { key: Date.now(), start: '13:00', end: '19:00', power: -200, state: '放电' }])}>新增时段</Button>
          </div>
          <Table size="small" rowKey="key" pagination={false} dataSource={manual}
            columns={[
              { title: '开始', dataIndex: 'start', render: (v, r) => <Input size="small" value={v} onChange={(e) => setManual(manual.map((x) => (x.key === r.key ? { ...x, start: e.target.value } : x)))} style={{ width: 90 }} /> },
              { title: '结束', dataIndex: 'end', render: (v, r) => <Input size="small" value={v} onChange={(e) => setManual(manual.map((x) => (x.key === r.key ? { ...x, end: e.target.value } : x)))} style={{ width: 90 }} /> },
              { title: '功率 (kW)', dataIndex: 'power', render: (v, r) => <InputNumber size="small" value={v} onChange={(val) => setManual(manual.map((x) => (x.key === r.key ? { ...x, power: val, state: val > 0 ? '充电' : val < 0 ? '放电' : '待机' } : x)))} style={{ width: 110 }} /> },
              { title: '状态', dataIndex: 'state', render: (v) => <Tag color={v === '充电' ? 'cyan' : v === '放电' ? 'green' : 'default'}>{v}</Tag> },
              { title: '操作', render: (_, r) => <Button size="small" type="link" danger onClick={() => setManual(manual.filter((x) => x.key !== r.key))}>删除</Button> },
            ]}
          />
          {!on ? <div className="peak-note">当前策略未启用，保存后需开启「策略启停」才会下发。</div> : null}
        </div>
      ) : null}

      {mode === 'dayahead' ? (
        <div>
          <div className="peak-toolbar">
            <span className="peak-toolbar-label">日前策略（本类型独有）</span>
            <Segmented value={daMode} onChange={setDaMode} options={[{ label: '阈值类型：固定值', value: 'fixed' }, { label: '算法自动规划', value: 'algo' }]} />
          </div>
          <div className="grid-2">
            <div>
              <div className="panel-title">当前日前电价策略配置内容</div>
              {daMode === 'fixed' ? (
                <div>
                  <div className="peak-form-row">
                    <span className="peak-form-label">充电电价阈值 (元/kWh)</span>
                    <InputNumber size="small" value={da.chargePrice} step={0.01} onChange={(v) => setDa({ ...da, chargePrice: v })} style={{ width: 160 }} />
                  </div>
                  <div className="peak-form-row">
                    <span className="peak-form-label">放电电价阈值 (元/kWh)</span>
                    <InputNumber size="small" value={da.dischargePrice} step={0.01} onChange={(v) => setDa({ ...da, dischargePrice: v })} style={{ width: 160 }} />
                  </div>
                  <div className="peak-form-row">
                    <span className="peak-form-label">非充放动作</span>
                    <Select size="small" value={da.idleAction} onChange={(v) => setDa({ ...da, idleAction: v })} style={{ width: 160 }}
                      options={['待机', '静置', '按 SOC 区间充放'].map((v) => ({ value: v, label: v }))} />
                  </div>
                </div>
              ) : (
                <div className="form-grid">
                  <div className="form-item"><span className="form-item-label">规划模式</span><span className="form-item-value">经济性最优（算法自动规划）</span></div>
                  <div className="form-item"><span className="form-item-label">电价来源</span><span className="form-item-value">日前 15 分钟电价</span></div>
                </div>
              )}
              <div className="panel-title" style={{ marginTop: 16 }}>今日电价</div>
              <TariffTags />
            </div>
            <div>
              <div className="panel-title">今日策略<small>电价（阶梯）与充放功率</small></div>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={dayAhead.plan} margin={{ top: 10, right: 16, bottom: 0, left: -8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                  <XAxis dataKey="clock" interval={7} tick={TICK} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="l" tick={TICK} axisLine={false} tickLine={false} width={48} />
                  <YAxis yAxisId="r" orientation="right" tick={TICK} axisLine={false} tickLine={false} width={40} />
                  <Tooltip contentStyle={TT} />
                  <Legend {...LEGEND} />
                  <ReferenceLine yAxisId="l" y={0} stroke="rgba(255,255,255,0.35)" />
                  <Line yAxisId="l" type="stepAfter" dataKey="power" name="充放功率 kW" stroke="#52e8b0" strokeWidth={2} dot={false} />
                  <Line yAxisId="r" type="stepAfter" dataKey="price" name="日前电价 元/kWh" stroke="#f4c542" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ---------------- 设备控制（差异 #8） ----------------
const DeviceControlView = ({ station }) => {
  const groups = getControlActions(station);
  const dev = getDevices(station.id);
  const targets = { PCS: dev.pcs.map((p) => p.name), BMS: dev.racks.map((r) => r.name), 空调: dev.hvac.map((a) => a.name), 液冷机组: [dev.liquid.name], 光伏逆变器: dev.pv.map((p) => p.name) };
  return (
    <div>
      <div className="blk-note" style={{ marginBottom: 16 }}>
        <div className="blk-note-text">策略运行中下发设备控制会与策略冲突：请先停止策略，再执行设备控制。</div>
        <div className="blk-note-tag">提示</div>
      </div>
      {groups.map((g) => (
        <div className="panel" key={g.group} style={{ marginBottom: 16 }}>
          <div className="panel-title">
            {g.group}
            <Space style={{ float: 'right' }}>
              <Select size="small" defaultValue={(targets[g.group] || [g.group])[0]} style={{ width: 160 }}
                options={(targets[g.group] || [g.group]).map((t) => ({ value: t, label: t }))} />
              <Button size="small" className="panel-title-btn" onClick={() => message.warning('请先停止策略再下发控制指令')}>停止策略</Button>
            </Space>
          </div>
          <Space size={8} wrap>
            {g.items.map((it) => (
              <Button key={it} size="small" onClick={() => message.success(`${g.group}：${it} 指令已下发（演示）`)}>{it}</Button>
            ))}
          </Space>
        </div>
      ))}
      <div className="peak-note">上电顺序：BMS 先合主负接触器 → 再开 PCS；下电顺序：先关 PCS → 再分主负接触器。</div>
    </div>
  );
};

// ---------------- 电池保护 ----------------
const BatteryView = () => {
  const p = getProtection().battery;
  const [socHigh, setSocHigh] = useState(p.socHigh);
  const [socLow, setSocLow] = useState(p.socLow);
  const [tempOn, setTempOn] = useState(true);
  const [voltOn, setVoltOn] = useState(true);
  return (
    <div className="panel" style={{ maxWidth: 760 }}>
      <div className="panel-title">电池保护<small>SOC 为必选参数；单体温度、单体电压可选</small></div>
      <div className="peak-form-row">
        <span className="peak-form-label">单体电压上限 / 下限 (V)</span>
        <InputNumber size="small" value={p.cellVoltageHigh} style={{ width: 120 }} readOnly />
        <InputNumber size="small" value={p.cellVoltageLow} style={{ width: 120 }} readOnly />
      </div>
      <div className="peak-form-row">
        <span className="peak-form-label">SOC 上限 / 下限 (%)（必选）</span>
        <InputNumber size="small" value={socHigh} onChange={setSocHigh} style={{ width: 120 }} />
        <InputNumber size="small" value={socLow} onChange={setSocLow} style={{ width: 120 }} />
      </div>
      <div className="peak-form-row">
        <span className="peak-form-label">单体温度保护（可选）</span>
        <Switch size="small" checked={tempOn} onChange={setTempOn} style={{ marginRight: 12 }} />
        <InputNumber size="small" value={p.cellTempHigh} disabled={!tempOn} style={{ width: 120 }} />
        <InputNumber size="small" value={p.cellTempLow} disabled={!tempOn} style={{ width: 120 }} />
      </div>
      <div className="peak-form-row">
        <span className="peak-form-label">单体电压保护（可选）</span>
        <Switch size="small" checked={voltOn} onChange={setVoltOn} style={{ marginRight: 12 }} />
        <InputNumber size="small" value={p.cellVoltageHigh} disabled={!voltOn} style={{ width: 120 }} />
        <InputNumber size="small" value={p.cellVoltageLow} disabled={!voltOn} style={{ width: 120 }} />
      </div>
      <div className="peak-form-row">
        <span className="peak-form-label">触发规则</span>
        <span className="peak-note" style={{ marginTop: 0 }}>
          SOC ≥ 上限禁止充电；SOC ≤ 下限禁止放电；{tempOn ? ' 单体温度越限禁止充/放；' : ''}{voltOn ? ' 单体电压越限禁止充/放；' : ''}
        </span>
      </div>
      <div className="peak-form-row">
        <span className="peak-form-label">低电量动作</span>
        <Radio.Group size="small" defaultValue={p.lowPowerAction}>
          <Radio value="停止放电并转充电">停止放电并转充电</Radio>
          <Radio value="停止放电并静置">停止放电并静置</Radio>
        </Radio.Group>
      </div>
      <div className="peak-toolbar" style={{ marginBottom: 0 }}>
        <span className="peak-toolbar-label">单体电压/SOC 上下限采用 3 位小数精度展示（示例：3.650 V / 95 %）</span>
      </div>
    </div>
  );
};

// ---------------- 需量保护 / 逆功率保护 ----------------
const DemandView = () => {
  const d = getProtection().demand;
  const [limit, setLimit] = useState(d.limit);
  const [checked, setChecked] = useState(['总表（并网点）', '变压器 T1']);
  return (
    <div className="panel" style={{ maxWidth: 760 }}>
      <div className="panel-title">需量保护<small>需量＝变压器用电功率限值</small></div>
      <div className="peak-form-row">
        <span className="peak-form-label">保护变压器</span>
        <Checkbox.Group value={checked} onChange={setChecked} options={['总表（并网点）', '变压器 T1', '变压器 T2'].map((o) => ({ label: o, value: o }))} />
      </div>
      <div className="peak-form-row">
        <span className="peak-form-label">需量限制值 (kW)</span>
        <InputNumber size="small" value={limit} onChange={setLimit} style={{ width: 140 }} />
      </div>
      <div className="peak-form-row">
        <span className="peak-form-label">越限动作</span>
        <Radio.Group size="small" defaultValue="降低储能充电功率直到静置">
          <Radio value="降低储能充电功率直到静置">降低储能充电功率，直到静置</Radio>
          <Radio value="降低储能充电功率必要时放电">降低储能充电功率，必要时转放电</Radio>
        </Radio.Group>
      </div>
      <div className="peak-note">触发时优先降低储能充电功率；若仍越限则按所选动作进一步处理。</div>
    </div>
  );
};

const ReverseView = () => {
  const r = getProtection().reverse;
  const [limit, setLimit] = useState(r.limit);
  const [checked, setChecked] = useState(['总表（并网点）']);
  return (
    <div className="panel" style={{ maxWidth: 760 }}>
      <div className="panel-title">逆功率保护<small>防止向电网反送电</small></div>
      <div className="peak-form-row">
        <span className="peak-form-label">保护变压器</span>
        <Checkbox.Group value={checked} onChange={setChecked} options={['总表（并网点）', '变压器 T1', '变压器 T2'].map((o) => ({ label: o, value: o }))} />
      </div>
      <div className="peak-form-row">
        <span className="peak-form-label">逆功率限制值 (kW)</span>
        <InputNumber size="small" value={limit} onChange={setLimit} style={{ width: 140 }} />
      </div>
      <div className="peak-form-row">
        <span className="peak-form-label">越限动作</span>
        <Radio.Group size="small" defaultValue="降低储能放电功率直到静置">
          <Radio value="降低储能放电功率直到静置">降低储能放电功率，直到静置</Radio>
          <Radio value="降低储能放电功率必要时充电">降低储能放电功率，必要时转充电</Radio>
        </Radio.Group>
      </div>
      <div className="peak-note">触发时优先降低储能放电功率；若仍越限则按所选动作进一步处理。</div>
    </div>
  );
};

// ---------------- 高级功能 ----------------
const AdvancedView = () => {
  const f = getAdvancedFeatures();
  const [sw, setSw] = useState({
    period: f.periodProtect.enabled, dynamic: f.dynamicMulti.enabled, varC: f.varControl.enabled,
    soc: f.periodSoc.enabled, weight: f.weightAlloc.enabled, degrade: f.degradeProtect.enabled, low: f.lowPower.enabled,
  });
  const toggle = (k) => (v) => setSw({ ...sw, [k]: v });
  return (
    <div>
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-title">1. 时段需量保护 / 时段逆功率保护<Switch size="small" checked={sw.period} onChange={toggle('period')} style={{ float: 'right' }} /></div>
        <Table size="small" rowKey="key" pagination={false} dataSource={f.periodProtect.rows}
          columns={[
            { title: '时段', dataIndex: 'period' },
            { title: '需量限制', dataIndex: 'demand' },
            { title: '逆功率限制', dataIndex: 'reverse' },
          ]}
        />
      </div>
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-title">2. 动态多级保护<Switch size="small" checked={sw.dynamic} onChange={toggle('dynamic')} style={{ float: 'right' }} /></div>
        <div className="form-grid">
          <div className="form-item"><span className="form-item-label">合同类型</span><span className="form-item-value">{f.dynamicMulti.contract}</span></div>
          <div className="form-item"><span className="form-item-label">月度最大需量</span><span className="form-item-value">{f.dynamicMulti.monthlyMaxDemand} kW</span></div>
        </div>
      </div>
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-title">3. 无功控制<Switch size="small" checked={sw.varC} onChange={toggle('varC')} style={{ float: 'right' }} /></div>
        <div className="peak-form-row">
          <span className="peak-form-label">无功模式</span>
          <Radio.Group size="small" defaultValue={f.varControl.mode}>
            <Radio value="固定无功">固定无功</Radio>
            <Radio value="固定功率因数">固定功率因数</Radio>
            <Radio value="电压越限调节">电压越限调节</Radio>
          </Radio.Group>
        </div>
        <div className="form-grid">
          <div className="form-item"><span className="form-item-label">固定无功</span><span className="form-item-value">{f.varControl.fixedVar} kVar</span></div>
          <div className="form-item"><span className="form-item-label">固定功率因数</span><span className="form-item-value">{f.varControl.fixedPf}</span></div>
          <div className="form-item"><span className="form-item-label">电压合格区间</span><span className="form-item-value">{f.varControl.standard} V</span></div>
          <div className="form-item"><span className="form-item-label">低压/高压调节</span><span className="form-item-value">{f.varControl.lowV}V→{f.varControl.lowVar}kVar / {f.varControl.highV}V→{f.varControl.highVar}kVar</span></div>
        </div>
      </div>
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-title">4. 时段 SOC 上下限<Switch size="small" checked={sw.soc} onChange={toggle('soc')} style={{ float: 'right' }} /></div>
        <Table size="small" rowKey="key" pagination={false} dataSource={f.periodSoc.rows}
          columns={[
            { title: '时段', dataIndex: 'period' },
            { title: 'SOC 上限 (%)', dataIndex: 'socHigh' },
            { title: 'SOC 下限 (%)', dataIndex: 'socLow' },
          ]}
        />
      </div>
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-title">5. 储能功率按权重分配<Switch size="small" checked={sw.weight} onChange={toggle('weight')} style={{ float: 'right' }} /></div>
        <Table size="small" rowKey="key" pagination={false} dataSource={f.weightAlloc.weights}
          columns={[
            { title: '设备', dataIndex: 'name' },
            { title: '权重', dataIndex: 'weight' },
          ]}
        />
      </div>
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-title">6. 电池降功率保护<Switch size="small" checked={sw.degrade} onChange={toggle('degrade')} style={{ float: 'right' }} /></div>
        <Table size="small" rowKey="key" pagination={false} dataSource={f.degradeProtect.rules}
          columns={[
            { title: '类型', dataIndex: 'type' },
            { title: '触发条件', dataIndex: 'condition' },
            { title: '降功率至', dataIndex: 'power' },
          ]}
        />
      </div>
      <div className="panel">
        <div className="panel-title">7. 掉电保护（储能电池低电保护）<Switch size="small" checked={sw.low} onChange={toggle('low')} style={{ float: 'right' }} /></div>
        <div className="form-grid">
          <div className="form-item"><span className="form-item-label">触发电压</span><span className="form-item-value">{f.lowPower.voltage} V</span></div>
          <div className="form-item"><span className="form-item-label">补电目标</span><span className="form-item-value">{f.lowPower.target} V</span></div>
          <div className="form-item"><span className="form-item-label">补电功率</span><span className="form-item-value">{f.lowPower.power} kW</span></div>
        </div>
      </div>
    </div>
  );
};

const PeakControl = ({ station }) => {
  const items = [
    { key: 'strategy', label: '策略管理', children: <StrategyView station={station} /> },
    { key: 'device', label: '设备控制', children: <DeviceControlView station={station} /> },
    { key: 'battery', label: '电池保护', children: <BatteryView /> },
    { key: 'demand', label: '需量保护', children: <DemandView /> },
    { key: 'reverse', label: '逆功率保护', children: <ReverseView /> },
    { key: 'advanced', label: '高级功能', children: <AdvancedView /> },
  ];
  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return <ScrollTabs items={items} />;
};

export default PeakControl;
